import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Badge, ButtonGroup } from "react-bootstrap";
import swal from "sweetalert";
import { Link } from "react-router-dom";

import {
  CitoPlusLinkIconDanger,
  LinkPrimary,
} from "jsx/components/CitoPlus/components/CitoPlusLink";

import { ResidenteService } from "services/ResidenteService";
import { getDefaultParams } from "services/DefaultParamets";
import Tables from "jsx/components/CitoPlus/components/tables";
import SearchInput from "jsx/components/CitoPlus/components/input_search";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

import "../../../table/FilteringTable/filtering.css";

const ROW_IMAGE_WIDTH = "120px";

const tableRowStyles = `
  .mis-reservas-table tbody tr {
    position: relative;
  }
  .mis-reservas-table tbody tr:hover {
    z-index: 1;
  }
  .mis-reservas-table tbody tr > td:first-child {
    width: 0;
    padding: 0;
    border: none;
  }
`;

const rowImageStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  bottom: 0,
  width: ROW_IMAGE_WIDTH,
  backgroundSize: "cover",
  backgroundPosition: "center",
  WebkitMaskImage: "linear-gradient(to right, black 65%, transparent 100%)",
  maskImage: "linear-gradient(to right, black 65%, transparent 100%)",
  zIndex: 0,
};

const rowNoImageStyle = {
  ...rowImageStyle,
  background: "#e9ecef",
};

const getEstadoBadge = (estado) => {
  const badges = {
    confirmada: <Badge bg="success">Confirmada</Badge>,
    pendiente: <Badge bg="warning">Pendiente</Badge>,
    cancelada: <Badge bg="danger">Cancelada</Badge>,
    incumplida: <Badge bg="secondary">Incumplida</Badge>,
  };
  return badges[estado] || <Badge bg="secondary">{estado}</Badge>;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split(" ");
  if (parts.length === 0) return "-";
  const dateParts = parts[0].split("/");
  if (dateParts.length !== 3) return "-";
  const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
  return date.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split(" ");
  if (parts.length < 2) return "-";
  const timeParts = parts[1].split(":");
  if (timeParts.length < 2) return "-";
  const date = new Date(2000, 0, 1, timeParts[0], timeParts[1]);
  return date.toLocaleTimeString("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MisReservas = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [defaultParams] = useState(getDefaultParams);

  const handleCancel = async (uuid, titulo) => {
    const willCancel = await swal({
      title: "¿Estás seguro?",
      text: `¿Deseas cancelar la reserva "${titulo}"?`,
      icon: "warning",
      buttons: ["No, mantener", "Sí, cancelar"],
      dangerMode: true,
    });

    if (willCancel) {
      try {
        await ResidenteService.cancelReserva(uuid);
        notifySuccess("Reserva cancelada correctamente");
        fetchUrl(currentPage, searchValue);
      } catch (error) {
        notifyError("Error al cancelar la reserva");
        handleErrors(error);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "imagen",
        Header: "",
        disableFilters: true,
        disableSortBy: true,
        accessor: (row) => {
          const imageStyle = row.imagen
            ? { ...rowImageStyle, backgroundImage: `url(${row.imagen})` }
            : rowNoImageStyle;
          return <div style={imageStyle} title={row.zona_nombre} />;
        },
      },
      {
        id: "zona",
        Header: "Zona",
        disableFilters: true,
        accessor: (row) => (
            <div style={{ paddingLeft: ROW_IMAGE_WIDTH }}>
              <div className="fw-semibold">{row.zona_nombre}</div>
              {row.all_day ? (
                <Badge bg="primary" pill className="mt-1">
                  Por día
                </Badge>
              ) : (
                <Badge bg="info" pill className="mt-1">
                  Por hora
                </Badge>
              )}
            </div>
        ),
      },
      {
        id: "fecha",
        Header: "Fecha",
        disableFilters: true,
        disableSortBy: false,
        accessor: (row) => (
          <div>
            <div className="fw-semibold">{formatDate(row.start)}</div>
            {!row.all_day && (
              <small className="text-muted">
                {formatTime(row.start)} - {formatTime(row.end)}
              </small>
            )}
            {row.all_day && <small className="text-muted">Todo el día</small>}
          </div>
        ),
      },
      {
        id: "titulo",
        Header: "Título",
        disableFilters: true,
        accessor: (row) => (
          <div>
            <span>{row.titulo}</span>
            {row.descripcion && (
              <small
                className="text-muted d-block text-truncate"
                style={{ maxWidth: "150px" }}
              >
                {row.descripcion}
              </small>
            )}
          </div>
        ),
      },
      {
        id: "estado",
        Header: "Estado",
        disableFilters: true,
        disableSortable: true,
        accessor: (row) => getEstadoBadge(row.estado),
      },
      {
        id: "actions",
        Header: "",
        disableFilters: true,
        disableSortable: true,
        accessor: (row) => {
          const canCancel =
            row.estado === "pendiente" || row.estado === "confirmada";
          if (!canCancel) return null;
          return (
            <ButtonGroup>
              <CitoPlusLinkIconDanger
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleCancel(row.uuid, row.titulo);
                }}
                title="Cancelar reserva"
              >
                <i className="fas fa-times-circle"></i>
              </CitoPlusLinkIconDanger>
            </ButtonGroup>
          );
        },
      },
    ],
    [],
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedfetchUrl = useCallback(
    debounce((pageIndex, search) => {
      fetchUrl(pageIndex, search);
    }, 500),
    [],
  );

  const fetchUrl = useCallback(
    async (pageIndex = 1, search = "") => {
      setLoading(true);
      try {
        const params = {
          ...defaultParams,
          page: pageIndex,
          search,
        };

        if (sortField) {
          params.ordering = order === "asc" ? sortField : `-${sortField}`;
        }

        const response = await ResidenteService.getReservas(params);
        setNextUrl(response.data.next);
        setPreviousUrl(response.data.previous);
        setTotalPages(response.data.total_pages);
        setCurrentPage(response.data.current_page);
        setData(response.data.results);
      } catch (error) {
        handleErrors(error);
        notifyError("Error al cargar las reservas");
      } finally {
        setLoading(false);
      }
    },
    [defaultParams, sortField, order],
  );

  useEffect(() => {
    fetchUrl(1, searchValue);
  }, []);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setLoading(true);
    debouncedfetchUrl(1, value);
  };

  const handleEnterPress = () => {
    fetchUrl(1, searchValue);
  };

  const handleClearClick = () => {
    setSearchValue("");
    fetchUrl(1, "");
  };

  const handleSortingChange = (column) => {
    const { id } = column;
    const newOrder =
      sortField === id ? (order === "asc" ? "desc" : "asc") : "asc";

    setSortField(id);
    setOrder(newOrder);
    setLoading(true);
    fetchUrl(1, searchValue, `${newOrder === "desc" ? "-" : ""}${id}`);
  };

  return (
    <>
      <style>{tableRowStyles}</style>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Mis Reservas</h4>
        <LinkPrimary to="/zonas-sociales" className="ms-auto">
          <i className="fas fa-plus me-2"></i>
          Nueva Reserva
        </LinkPrimary>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-tools">
            <SearchInput
              value={searchValue}
              onSearchChange={handleSearchChange}
              onEnterPress={handleEnterPress}
              onClearClick={handleClearClick}
            />
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <SpinnerCitoPlus text="Cargando reservas..." />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-calendar-check fa-3x text-muted mb-3"></i>
              <h5>No hay reservas</h5>
              <p className="text-muted">
                Aquí aparecerán tus reservas de zonas sociales.
              </p>
              <LinkPrimary to="/zonas-sociales">
                <i className="fas fa-plus me-2"></i>
                Hacer una reserva
              </LinkPrimary>
            </div>
          ) : (
            <div className="mis-reservas-table">
              <Tables
                columns={columns}
                data={data}
                loading={loading}
                totalPages={totalPages}
                currentPage={currentPage}
                fetchUrl={fetchUrl}
                handleSortingChange={handleSortingChange}
                nextUrl={nextUrl}
                previousUrl={previousUrl}
                sortField={sortField}
                order={order}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MisReservas;
